import * as React from 'react';

import { LoggedInUserReviewQuery } from 'packages/shared/definitions/types';

import {
  ModalModel,
  ModalContext,
  IModalProps,
} from '@notino/react-styleguide';

import {
  ReviewSourceTypes,
  IRating,
} from '@containers/ProductDetailContainer/model';
import { useUser } from '@queries/useUser';

import { shouldOpenReviewModal } from '../../utils/shouldOpenReviewModal';

import { ReviewsForm } from './ReviewsFormModalBody/loadable';
import { ReviewsSentSuccessModalBody } from './ReviewsSentSuccesModal';

interface IUseReviewModalParameters {
  ratings: IRating[];
  productCode: string;
  masterId: string;
  catalogMasterId: string;
  isAllReviewsPage?: boolean;
  loggedInUserReview?: LoggedInUserReviewQuery['loggedInUserReview'];
  refetchLoggedInUserReview: () => Promise<void>;
}

const MODAL_OPTIONS: IModalProps = {
  size: ModalModel.Sizes.default,
  type: ModalModel.Types.default,
  header: null,
  center: false,
  noBorders: true,
  withFocusTrap: true,
};

export const useReviewModal = ({
  ratings,
  productCode,
  masterId,
  catalogMasterId,
  isAllReviewsPage,
  loggedInUserReview,
  refetchLoggedInUserReview,
}: IUseReviewModalParameters) => {
  const { toggleModal, isShown } = ModalContext.useModalContext();
  const [success, setSuccess] = React.useState(false);
  const { isLoggedIn } = useUser();

  const handleSuccess = React.useCallback(async () => {
    setSuccess(true);
    if (isLoggedIn) {
      await refetchLoggedInUserReview();
    }
  }, [refetchLoggedInUserReview, isLoggedIn]);

  React.useEffect(() => {
    // reset success when modal closed so user can edit review when opened again
    if (!isShown && isLoggedIn) {
      setSuccess(false);
    }
  }, [isShown, isLoggedIn]);

  React.useEffect(() => {
    const reviewSource = shouldOpenReviewModal();
    if (reviewSource) {
      toggleModal(
        <div tabIndex={0}>
          <ReviewsForm
            catalogMasterId={catalogMasterId}
            masterId={masterId}
            ratings={ratings}
            source={reviewSource}
            productCode={productCode}
            onSuccess={handleSuccess}
            loggedInUserReview={loggedInUserReview}
          />
        </div>,
        MODAL_OPTIONS
      );
    }
  }, [
    masterId,
    catalogMasterId,
    productCode,
    ratings,
    toggleModal,
    handleSuccess,
    loggedInUserReview,
  ]);

  return React.useCallback(() => {
    toggleModal(
      <div tabIndex={0}>
        {success ? (
          <ReviewsSentSuccessModalBody />
        ) : (
          <ReviewsForm
            masterId={masterId}
            catalogMasterId={catalogMasterId}
            ratings={ratings}
            source={ReviewSourceTypes.DIRECT}
            productCode={productCode}
            isAllReviewsPage={isAllReviewsPage}
            onSuccess={handleSuccess}
            loggedInUserReview={loggedInUserReview}
          />
        )}
      </div>,
      MODAL_OPTIONS
    );
  }, [
    masterId,
    catalogMasterId,
    productCode,
    ratings,
    toggleModal,
    success,
    isAllReviewsPage,
    handleSuccess,
    loggedInUserReview,
  ]);
};
